// Components/Billing/PaymentHistory.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, CreditCard, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { getPaymentHistory } from '../../Fetch/fetchSubscription';
import styles from './PaymentHistory.module.css';

const PaymentHistory = ({ buildingId }) => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPaymentHistory();
    }, [buildingId]);

    const fetchPaymentHistory = async () => {
        try {
            setLoading(true);
            const response = await getPaymentHistory(buildingId);
            if (response.success) {
                setPayments(response.payments || []);
            } else {
                setError('שגיאה בטעינת היסטוריית התשלומים');
            }
        } catch (error) {
            setError('שגיאה בטעינת היסטוריית התשלומים');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Completed':
                return <CheckCircle className={styles.statusIconSuccess} />;
            case 'Failed':
                return <AlertCircle className={styles.statusIconError} />;
            case 'Pending':
                return <Calendar className={styles.statusIconPending} />;
            default:
                return <AlertCircle className={styles.statusIconDefault} />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'Completed': return 'הושלם';
            case 'Failed': return 'נכשל';
            case 'Pending': return 'ממתין';
            case 'Refunded': return 'הוחזר';
            default: return status;
        }
    };

    const getPaymentMethodText = (method) => {
        switch (method) {
            case 'CreditCard': return 'כרטיס אשראי';
            case 'BankTransfer': return 'העברה בנקאית';
            case 'Cash': return 'מזומן';
            default: return method;
        }
    };

    if (loading) {
        return (
            <div className={styles.paymentHistory} dir="rtl">
                <div className={styles.loading}>טוען היסטוריית תשלומים...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.paymentHistory} dir="rtl">
                <div className={styles.error}>{error}</div>
            </div>
        );
    }

    return (
        <div className={styles.paymentHistory} dir="rtl">
            <div className={styles.historyHeader}>
                <h3 className={styles.historyTitle}>
                    <Calendar className={styles.titleIcon} />
                    היסטוריית תשלומים
                </h3>
                {payments.length > 0 && (
                    <button className={styles.downloadButton}>
                        <Download size={16} />
                        הורד דוח
                    </button>
                )}
            </div>

            {payments.length === 0 ? (
                <div className={styles.emptyState}>
                    <CreditCard className={styles.emptyIcon} />
                    <h4>אין תשלומים עדיין</h4>
                    <p>כאשר תבצע תשלומים, הם יופיעו כאן</p>
                </div>
            ) : (
                <div className={styles.paymentsContainer}>
                    <div className={styles.paymentsHeader}>
                        <div className={styles.headerCell}>תאריך</div>
                        <div className={styles.headerCell}>סכום</div>
                        <div className={styles.headerCell}>אמצעי תשלום</div>
                        <div className={styles.headerCell}>סטטוס</div>
                        <div className={styles.headerCell}>מזהה עסקה</div>
                    </div>
                    
                    <div className={styles.paymentsList}>
                        {payments.map((payment) => (
                            <div key={payment.paymentAppId} className={styles.paymentRow}>
                                <div className={styles.paymentCell}>
                                    {new Date(payment.paymentDate).toLocaleDateString('he-IL')}
                                </div>
                                <div className={styles.paymentCell}>
                                    <span className={styles.amount}>₪{payment.amount}</span>
                                </div>
                                <div className={styles.paymentCell}>
                                    {getPaymentMethodText(payment.paymentMethod)}
                                </div>
                                <div className={styles.paymentCell}>
                                    <div className={styles.statusContainer}>
                                        {getStatusIcon(payment.paymentStatus)}
                                        <span>{getStatusText(payment.paymentStatus)}</span>
                                    </div>
                                </div>
                                <div className={styles.paymentCell}>
                                    <span className={styles.transactionId}>
                                        {payment.transactionId || 'לא זמין'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentHistory;